// Чистая логика вкладки «Анализ комплектности РД» (№7): подписи, разбор
// доказательств находки, группировка по видам работ и по системам, фильтры.
// Без импортов значений (только `import type`) — проверяется встроенным
// node:test (см. logic.test.mjs).
import type {
  PtoRdGapAiPlan,
  PtoRdGapFinding,
  PtoRdGapRunBrief,
} from '../../../shared/api/endpoints/ptoRdGaps';
import type { SheetTarget } from '../rdSheet/protocol';

type Json = Record<string, unknown>;

function asObj(v: unknown): Json | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Json) : null;
}

function asArr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Число для инженера: до 4 знаков, разделитель — запятая. */
export function fmtNum(v: number | null | undefined, digits = 4): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return v.toLocaleString('ru-RU', { maximumFractionDigits: digits });
}

export const KIND_LABEL: Record<string, string> = {
  aux_material: 'материал нормы ГЭСН',
  kit_material: 'по типовому комплекту',
  missing_material: 'недостающий материал',
  missing_work: 'недостающая работа',
  work_without_material: 'работа без изделия',
  material_without_work: 'изделие без работы',
  qty_mismatch: 'расхождение количеств',
  incomparable: 'несравнимо',
};

export const LAYER_LABEL: Record<string, string> = {
  rule: 'типовой комплект',
  norm: 'нормы ГЭСН',
  reconcile: 'сверка ВОР↔СП',
  ai: 'ИИ',
};

export const STATUS_LABEL: Record<string, string> = {
  proposed: 'предложено',
  doubtful: 'сомнительно',
  accepted: 'принято',
  rejected: 'отклонено',
  stale: 'устарело',
};

export function presenceLabel(p: string | null): string | null {
  switch (p) {
    case 'absent':
      return 'нет в СП';
    case 'unclear':
      return 'неясно';
    default:
      return null;
  }
}

export function visibilityNote(v: string): string | null {
  switch (v) {
    case 'hidden_consumable':
      return 'скрыто: расходник подрядчика';
    case 'hidden_obsolete':
      return 'скрыто: устаревший ресурс';
    default:
      return null;
  }
}

/** Строка ВОР в доказательстве находки. */
export interface EvidenceRow {
  id: string | null;
  uid: string | null;
  page: number | null;
  sourceRef: string | null;
  name: string;
  qty: number | null;
  unit: string | null;
  system: string | null;
  norm: string | null;
  normUnit: string | null;
  qNorm: number | null;
  perUnit: number | null;
  estimate: number | null;
  formula: string | null;
  refineNote: string | null;
  sim: number | null;
}

function parseRow(v: unknown): EvidenceRow | null {
  const r = asObj(v);
  if (!r) return null;
  return {
    id: str(r.id) ?? str(r.position_id),
    uid: str(r.uid) ?? str(r.record_uid),
    page: num(r.page),
    sourceRef: str(r.source_ref),
    name: str(r.name) ?? '',
    qty: num(r.qty) ?? num(r.quantity),
    unit: str(r.unit),
    system: str(r.sys),
    norm: str(r.norm),
    normUnit: str(r.norm_unit),
    qNorm: num(r.q_norm),
    perUnit: num(r.per_unit),
    estimate: num(r.estimate),
    formula: str(r.formula),
    refineNote: str(r.refine_note),
    sim: num(r.sim),
  };
}

function parseRows(v: unknown): EvidenceRow[] {
  return asArr(v)
    .map(parseRow)
    .filter((r): r is EvidenceRow => r !== null);
}

/** Норма нормы-основания: шифр, расход на единицу, строк, оценка. */
export interface NormRef {
  code: string;
  name: string;
  unit: string | null;
  perUnit: number | null;
  rows: number;
  estimate: number | null;
  content: string[];
}

export function normRefs(f: PtoRdGapFinding): NormRef[] {
  return asArr(f.norm_refs)
    .map((v) => {
      const r = asObj(v);
      if (!r || !str(r.code)) return null;
      return {
        code: str(r.code) ?? '',
        name: str(r.name) ?? '',
        unit: str(r.unit),
        perUnit: num(r.per_unit),
        rows: num(r.rows) ?? 0,
        estimate: num(r.estimate),
        content: asArr(r.content).filter((c): c is string => typeof c === 'string'),
      };
    })
    .filter((r): r is NormRef => r !== null);
}

/** Доказательства находки в удобном виде. */
export interface Evidence {
  rows: EvidenceRow[];
  rowsWithoutQty: EvidenceRow[];
  kitRowsBeyondNorm: EvidenceRow[];
  presenceNotes: string[];
  presenceHits: EvidenceRow[];
  presenceOther: { row: EvidenceRow; why: string }[];
  searchedRows: number | null;
  kitRule: { workLabel: string; material: string } | null;
  /** Сверка ВОР↔СП: стороны с суммами. */
  reconcile: {
    vor: {
      sum: number | null;
      unit: string | null;
      rows: EvidenceRow[];
      noQty: EvidenceRow[];
      otherUnits: EvidenceRow[];
    };
    sp: {
      sum: number | null;
      unit: string | null;
      rows: EvidenceRow[];
      noQty: EvidenceRow[];
      otherUnits: EvidenceRow[];
    };
    deltaPct: number | null;
  } | null;
}

function side(v: unknown) {
  const s = asObj(v) ?? {};
  return {
    sum: num(s.sum),
    unit: str(s.unit),
    rows: parseRows(s.listed),
    noQty: parseRows(s.no_qty),
    otherUnits: parseRows(s.other_units),
  };
}

export function evidenceOf(f: PtoRdGapFinding): Evidence {
  const e = asObj(f.evidence) ?? {};
  const presence = asObj(e.presence) ?? {};
  const kit = asObj(e.kit_rule);
  return {
    rows: parseRows(e.rows),
    rowsWithoutQty: parseRows(e.rows_without_qty),
    kitRowsBeyondNorm: parseRows(e.kit_rows_beyond_norm),
    presenceNotes: asArr(presence.notes).filter((n): n is string => typeof n === 'string'),
    presenceHits: parseRows(presence.hits),
    presenceOther: asArr(presence.other)
      .map((v) => {
        const row = parseRow(v);
        const why = str(asObj(v)?.why);
        return row && why ? { row, why } : null;
      })
      .filter((x): x is { row: EvidenceRow; why: string } => x !== null),
    searchedRows: num(presence.searched_rows),
    kitRule: kit
      ? { workLabel: str(kit.work_label) ?? '', material: str(kit.material) ?? '' }
      : null,
    reconcile:
      f.layer === 'reconcile'
        ? { vor: side(e.vor), sp: side(e.sp), deltaPct: num(e.delta_pct) }
        : null,
  };
}

/** Фильтры списка находок. */
export interface FindingFilter {
  showHidden: boolean;
  /** `all` — все статусы. */
  status: 'all' | 'proposed' | 'accepted' | 'rejected';
  /** Только находки по строке ВОР (record_uid) — переход со значка «⚠ N». */
  rowUid: string | null;
}

export function filterFindings(
  findings: PtoRdGapFinding[],
  filter: FindingFilter,
): PtoRdGapFinding[] {
  return findings.filter(
    (f) =>
      (filter.showHidden || f.visibility === 'show') &&
      (filter.status === 'all' || f.status === filter.status) &&
      (filter.rowUid === null || f.related_record_uids.includes(filter.rowUid)),
  );
}

/** Группа находок по виду работ (таблица ГЭСН, правило или сверка). */
export interface FindingGroup {
  key: string;
  label: string;
  items: PtoRdGapFinding[];
}

/** По видам работ: порядок групп — как пришли (по ключу), сверка ВОР↔СП — в
 *  конце; внутри группы — видимые первыми, затем по убыванию оценки. */
export function groupByWorkKind(findings: PtoRdGapFinding[]): FindingGroup[] {
  const map = new Map<string, FindingGroup>();
  for (const f of findings) {
    const g = map.get(f.group_key) ?? { key: f.group_key, label: f.group_label, items: [] };
    g.items.push(f);
    map.set(f.group_key, g);
  }
  const groups = [...map.values()];
  for (const g of groups) {
    g.items.sort(
      (a, b) =>
        Number(a.visibility !== 'show') - Number(b.visibility !== 'show') ||
        (b.qty_estimate ?? -1) - (a.qty_estimate ?? -1) ||
        a.name.localeCompare(b.name, 'ru'),
    );
  }
  return groups.sort(
    (a, b) =>
      Number(a.key === 'reconcile') - Number(b.key === 'reconcile') ||
      a.label.localeCompare(b.label, 'ru'),
  );
}

/** Находка в разрезе системы: оценка — сумма строк этой системы. */
export interface SystemItem {
  finding: PtoRdGapFinding;
  estimate: number | null;
  rows: number;
}

export interface SystemGroup {
  system: string;
  items: SystemItem[];
}

export const NO_SYSTEM = 'система не указана';

/** По системам: строки находки раскладываются по своим системам; находки без
 *  строк ВОР (сверка) — отдельной группой. Системы — по алфавиту, «не
 *  указана» — в конце (пока системы не распознаются, №3, она большая). */
export function groupBySystem(findings: PtoRdGapFinding[]): SystemGroup[] {
  const map = new Map<string, Map<string, SystemItem>>();
  const add = (system: string, f: PtoRdGapFinding, est: number | null) => {
    const items = map.get(system) ?? new Map<string, SystemItem>();
    const it = items.get(f.id) ?? { finding: f, estimate: null, rows: 0 };
    it.rows += 1;
    if (est !== null) it.estimate = (it.estimate ?? 0) + est;
    items.set(f.id, it);
    map.set(system, items);
  };
  for (const f of findings) {
    if (f.layer === 'reconcile') {
      const items = map.get('Сверка ВОР↔СП') ?? new Map<string, SystemItem>();
      items.set(f.id, { finding: f, estimate: null, rows: 0 });
      map.set('Сверка ВОР↔СП', items);
      continue;
    }
    const ev = evidenceOf(f);
    const rows = [...ev.rows, ...ev.rowsWithoutQty, ...ev.kitRowsBeyondNorm];
    if (rows.length === 0) add(NO_SYSTEM, f, null);
    for (const r of rows) add(r.system ?? NO_SYSTEM, f, hasNormEstimate(f) ? r.estimate : null);
  }
  const rank = (s: string) => (s === 'Сверка ВОР↔СП' ? 2 : s === NO_SYSTEM ? 1 : 0);
  return [...map.entries()]
    .map(([system, items]) => ({
      system,
      items: [...items.values()].sort(
        (a, b) =>
          Number(a.finding.visibility !== 'show') - Number(b.finding.visibility !== 'show') ||
          (b.estimate ?? -1) - (a.estimate ?? -1),
      ),
    }))
    .sort(
      (a, b) =>
        rank(a.system) - rank(b.system) ||
        a.system.localeCompare(b.system, 'ru', { numeric: true }),
    );
}

/** Итог по ресурсу комплекта из сводки прогона. */
export interface TotalRow {
  resourceCode: string;
  name: string;
  unit: string | null;
  className: string;
  visibility: string;
  presence: string;
  total: number | null;
  rows: number;
  norms: string[];
  /** Строки СП, у которых оценка материала (ЛКМ, который в СП есть). */
  spRows: string[];
}

export function totalsOf(summary: Record<string, unknown> | null | undefined): TotalRow[] {
  return asArr(summary?.totals)
    .map((v) => {
      const t = asObj(v);
      if (!t) return null;
      return {
        resourceCode: str(t.resource_code) ?? '',
        name: str(t.name) ?? '',
        unit: str(t.unit),
        className: str(t.class) ?? '',
        visibility: str(t.visibility) ?? 'show',
        presence: str(t.presence) ?? '',
        total: num(t.total),
        rows: num(t.rows) ?? 0,
        norms: asArr(t.norms).filter((n): n is string => typeof n === 'string'),
        spRows: asArr(t.sp_rows).filter((n): n is string => typeof n === 'string'),
      };
    })
    .filter((t): t is TotalRow => t !== null);
}

/** Строка охвата: сколько строк ВОР сопоставлено с нормами. */
export function coverageText(summary: Record<string, unknown> | null | undefined): string | null {
  const c = asObj(summary?.coverage);
  if (!c) return null;
  const n = (k: string) => num(c[k]) ?? 0;
  const parts = [
    `строк ВОР ${n('vor_primary')} (без повторов)`,
    `сопоставлено с ГЭСН ${n('matched')}`,
  ];
  if (n('refined_by_size') > 0)
    parts.push(`строка нормы уточнена по размеру — ${n('refined_by_size')}`);
  if (n('below_threshold') > 0) parts.push(`работ ниже порога 0,85 — ${n('below_threshold')}`);
  if (n('matched_without_qty') > 0) parts.push(`без количества — ${n('matched_without_qty')}`);
  if (n('units_incompatible') > 0) parts.push(`единицы несопоставимы — ${n('units_incompatible')}`);
  return parts.join(' · ');
}

/** Принятые находки, ещё не вошедшие в запрос SQ. */
export function acceptedNotInSq(findings: PtoRdGapFinding[]): number {
  return findings.filter((f) => f.status === 'accepted' && !f.sq_request_id).length;
}

/** Строка ВОР — для значка «⚠ N» (только таблицы ВОР). */
export function isVorRow(raw: unknown): boolean {
  return asObj(raw)?.table_kind === 'vor';
}

/** Значки «⚠ N» на экране позиций: `row_flags` анализа (record_uid строки →
 *  число находок) и переход на вкладку анализа с находками этой строки. */
export interface GapRowFlags {
  flags: Readonly<Record<string, number>>;
  onOpen: (recordUid: string) => void;
  /** Оценки по нормам у строк СП (ЛКМ): record_uid строки СП → оценки. */
  estimates?: Readonly<Record<string, SpEstimate[]>>;
}

/** Оценка «норма × объём» у строки СП (ЛКМ, план по замечаниям 23.10, п. 2
 *  «б»): материал нормы, который в СП есть, того же вида и материала, —
 *  «≈ X кг — оценка по ГЭСН …, сверить, не для заказа». В суммы не входит. */
export interface SpEstimate {
  /** Текст у количества строки — как прислал сервер. */
  text: string;
  /** Подсказка: материал нормы, сверка с СП, строки ВОР с формулой. */
  detail: string;
  estimate: number | null;
  unit: string | null;
  /** Норма выбрана ИИ (подбор норм), проверена программой. */
  byAi: boolean;
  /** Прогон устарел — позиции РД изменились после него. */
  stale: boolean;
}

/** Оценки у строк СП из записей сводки (`summary.sp_estimates` прогона или
 *  записи подбора норм ИИ): record_uid строки СП → оценки. */
export function spEstimatesOf(
  rows: unknown,
  opts: { byAi?: boolean; stale?: boolean } = {},
): Record<string, SpEstimate[]> {
  const out: Record<string, SpEstimate[]> = {};
  for (const v of asArr(rows)) {
    const r = asObj(v);
    const uid = str(r?.record_uid);
    if (!r || !uid) continue;
    for (const iv of asArr(r.items)) {
      const i = asObj(iv);
      const text = str(i?.text);
      if (!i || !text) continue;
      const vor = asArr(i.vor_rows)
        .map(asObj)
        .filter((x): x is Json => x !== null)
        .map((x) => `${str(x.row) ?? ''} «${str(x.name) ?? ''}»: ${str(x.formula) ?? ''}`.trim());
      const detail = [
        `Материал нормы: ${str(i.resource) ?? '—'} (${str(i.class) ?? ''})`,
        str(i.agreement),
        opts.byAi ? AI_NORM_LABEL : null,
        ...vor,
        str(i.note),
      ]
        .filter(Boolean)
        .join('\n');
      (out[uid] ??= []).push({
        text,
        detail,
        estimate: num(i.estimate),
        unit: str(i.unit),
        byAi: opts.byAi === true,
        stale: opts.stale === true,
      });
    }
  }
  return out;
}

/** Оценки экрана: последний прогон без ИИ и подбор норм последнего прогона ИИ. */
export function screenEstimates(
  data:
    | {
        run?: { summary?: unknown } | null;
        stale?: boolean;
        ai?: { run?: { summary?: unknown } | null; stale?: boolean } | null;
      }
    | null
    | undefined,
): Record<string, SpEstimate[]> {
  const base = spEstimatesOf(asObj(data?.run?.summary)?.sp_estimates, {
    stale: data?.stale,
  });
  const choice = asObj(asObj(data?.ai?.run?.summary)?.norm_choice);
  const ai = spEstimatesOf(choice?.sp_estimates, { byAi: true, stale: data?.ai?.stale });
  for (const [uid, list] of Object.entries(ai)) {
    (base[uid] ??= []).push(...list);
  }
  return base;
}

/** Оценки строки на экране позиций; анализ выключен или оценок нет — пусто. */
export function rowEstimates(
  p: { record_uid: string },
  gaps: Pick<GapRowFlags, 'estimates'> | null | undefined,
): SpEstimate[] {
  return gaps?.estimates?.[p.record_uid] ?? [];
}

/** Число у значка «⚠ N» строки: находки по строке ВОР; у прочих строк, без
 *  анализа и без находок — 0 (значка нет). */
export function rowGapCount(
  p: { raw: unknown; record_uid: string },
  flags: Readonly<Record<string, number>> | null | undefined,
): number {
  if (!flags || !isVorRow(p.raw)) return 0;
  const n = flags[p.record_uid];
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0;
}

// ── ИИ-слой (по кнопке) ──

export const AI_PROPOSAL_LABEL = 'предложение ИИ, проверено Flash';
export const AI_NORM_LABEL = 'норма выбрана ИИ, проверена программой';

/** Находка с оценкой «норма × объём» по строкам: слой норм или норма,
 *  выбранная ИИ. */
export function hasNormEstimate(f: PtoRdGapFinding): boolean {
  if (f.layer === 'norm') return true;
  return f.layer === 'ai' && str(asObj(asObj(f.evidence)?.ai)?.kind) === 'norm_choice';
}

/** Цитата из РД у предложения ИИ: как дала модель и место на листе. */
export interface AiQuote {
  page: number | null;
  sheetRef: string | null;
  text: string;
  before: string;
  matched: string;
  after: string;
}

/** ИИ-сведения находки. */
export interface AiInfo {
  kind: 'proposal' | 'norm_choice';
  label: string;
  why: string | null;
  verdictReason: string | null;
  groupLabel: string | null;
  quote: AiQuote | null;
}

export function aiInfo(f: PtoRdGapFinding): AiInfo | null {
  if (f.layer !== 'ai') return null;
  const e = asObj(f.evidence) ?? {};
  const ai = asObj(e.ai) ?? {};
  const normChoice = str(ai.kind) === 'norm_choice';
  const q = asObj(e.quote);
  return {
    kind: normChoice ? 'norm_choice' : 'proposal',
    label: normChoice ? AI_NORM_LABEL : AI_PROPOSAL_LABEL,
    why: str(e.why),
    verdictReason: str(ai.verdict_reason),
    groupLabel: str(ai.group_label),
    quote:
      q && str(q.text)
        ? {
            page: num(q.page),
            sheetRef: str(q.sheet_ref),
            text: str(q.text) ?? '',
            before: str(q.before) ?? '',
            matched: str(q.matched) ?? '',
            after: str(q.after) ?? '',
          }
        : null,
  };
}

/** Место цитаты: «стр. PDF 8 · л. 8» (лист — как прочитан в тексте листа). */
export function quotePlace(q: Pick<AiQuote, 'page' | 'sheetRef'>): string {
  if (q.page === null) return '';
  return `стр. PDF ${q.page}${q.sheetRef ? ` · л. ${q.sheetRef}` : ''}`;
}

/** Подсказка к «Источнику»: текст листа вокруг цитаты (сверен программой). */
export function quoteHint(q: Pick<AiQuote, 'page' | 'before' | 'matched' | 'after'>): string {
  const around = [q.before && `…${q.before}`, `«${q.matched}»`, q.after && `${q.after}…`]
    .filter(Boolean)
    .join(' ');
  const open = q.page === null ? '' : 'Открыть лист РД в окне листа.\n';
  return `${open}Текст листа вокруг цитаты (сверен программой): ${around}`;
}

/** Окно исходного листа РД (выпуск 1б, №9) для цитаты ИИ: лист ревизии на
 *  странице цитаты, без рамки (координат цитаты нет — подсветки нет); в шапке
 *  окна — сама цитата. Файл не указан: фрагменты текста листов пока без файла
 *  (происхождение — выпуск 3, A1), у однофайловой ревизии он и не нужен.
 *  Позиции у цитаты нет — и панели правки №8 в окне нет (`projectId: null`).
 *  Страницы нет — null (открывать нечего). */
export function quoteSheetTarget(
  revisionId: string,
  q: AiQuote,
  docCode: string | null,
): SheetTarget | null {
  if (!revisionId || q.page === null || !Number.isInteger(q.page) || q.page < 1) return null;
  return {
    revisionId,
    page: q.page,
    file: null,
    box: null,
    boxIsAnchor: false,
    headBox: null,
    headPage: null,
    positionId: null,
    projectId: null,
    title: `«${q.text}»`,
    place: quotePlace(q),
    qty: '',
    docCode,
  };
}

/** Деньги для инженера: микродоллары → «0,014 $». */
export function fmtUsd(micro: number | null | undefined): string {
  if (micro === null || micro === undefined || !Number.isFinite(micro)) return '—';
  const usd = micro / 1_000_000;
  const digits = usd === 0 ? 0 : usd < 0.01 ? 4 : usd < 1 ? 3 : 2;
  return `${usd.toLocaleString('ru-RU', { maximumFractionDigits: digits })} $`;
}

/** Оценка выбранного по плану (так же считает сервер): группы с актуальным
 *  ответом и подбор норм без строк — 0. */
export function selectionEstimate(
  plan: PtoRdGapAiPlan,
  groups: ReadonlySet<string>,
  normChoice: boolean,
): { usdMicro: number; calls: number; asked: number } {
  let usdMicro = 0;
  let calls = 0;
  let asked = 0;
  for (const g of plan.groups) {
    if (!groups.has(g.key) || g.unchanged) continue;
    usdMicro += g.estimate.usd_micro;
    calls += g.estimate.calls;
    asked += 1;
  }
  const n = plan.norm_choice;
  if (normChoice && !n.unchanged && n.unique > 0) {
    usdMicro += n.estimate.usd_micro;
    calls += n.estimate.calls;
    asked += 1;
  }
  return { usdMicro, calls, asked };
}

/** Отброшенное предложение ИИ (программой или Flash) — для пилота. */
export interface AiDropped {
  group: string;
  name: string;
  stage: string;
  reason: string;
}

/** Итог прогона ИИ из сводки: сколько предложено, оставлено, отброшено. */
export interface AiRunStats {
  proposals: number;
  kept: number;
  droppedByCode: number;
  droppedByFlash: number;
  normsPicked: number;
  dropped: AiDropped[];
}

export function aiRunStats(summary: Record<string, unknown> | null | undefined): AiRunStats {
  const out: AiRunStats = {
    proposals: 0,
    kept: 0,
    droppedByCode: 0,
    droppedByFlash: 0,
    normsPicked: 0,
    dropped: [],
  };
  for (const v of asArr(summary?.groups)) {
    const g = asObj(v);
    // Перенесённые из прошлых прогонов группы — не этого прогона.
    if (!g || g.reused === true) continue;
    out.proposals += num(g.proposals) ?? 0;
    out.kept += num(g.kept) ?? 0;
    for (const d of asArr(g.dropped)) {
      const o = asObj(d);
      if (!o) continue;
      const stage = str(o.stage) ?? 'code';
      if (stage === 'flash') out.droppedByFlash += 1;
      else out.droppedByCode += 1;
      out.dropped.push({
        group: str(g.label) ?? '',
        name: str(o.name) ?? '',
        stage,
        reason: str(o.reason) ?? '',
      });
    }
  }
  const n = asObj(summary?.norm_choice);
  if (n && n.reused !== true) {
    out.normsPicked = num(n.picked) ?? 0;
    for (const r of asArr(n.rejected)) {
      const o = asObj(r);
      if (!o) continue;
      out.droppedByCode += 1;
      out.dropped.push({
        group: 'подбор норм',
        name: str(o.row) ?? '',
        stage: 'code',
        reason: `${str(o.code) ?? ''}: ${str(o.reason) ?? ''}`,
      });
    }
  }
  return out;
}

/** Ход идущего прогона ИИ: «ИИ: 2 из 5 — Система В13». */
export function aiProgressText(p: PtoRdGapRunBrief | null | undefined): string | null {
  if (!p) return null;
  const pr = p.progress;
  if (!pr || !pr.total) return p.status === 'queued' ? 'ИИ: в очереди…' : 'ИИ: идёт…';
  const step = Math.min(pr.done + 1, pr.total);
  return `ИИ: ${step} из ${pr.total}${pr.current ? ` — ${pr.current}` : ''}`;
}
